/**
 * DigiWedge Shared Color Tokens
 * Single source of truth for color palette across web and mobile platforms.
 *
 * Usage:
 * - Web (tee-time-ui): Import and use with CSS variables or Tailwind
 * - Mobile (gluestack-ui): Import directly for React Native StyleSheet
 */
// =============================================================================
// Brand Colors
// =============================================================================
export const brand = {
    primary: '#6aae43',
    primaryLight: '#f8ffea',
    primaryHover: '#7ec454',
    primaryActive: '#5a9a38',
    secondary: '#1e3a5f',
    onPrimary: '#ffffff',
    onSecondary: '#ffffff',
};
export const brandAlpha = {
    primary10: 'rgba(106, 174, 67, 0.1)',
    primary15: 'rgba(106, 174, 67, 0.15)',
};
// =============================================================================
// YOLO Brand Colors
// =============================================================================
export const yolo = {
    // Primary brand colors
    navy: '#375093',
    navyDark: '#2a3d70',
    navyLight: '#4a6bb8',
    lime: '#C9DD51',
    limeDark: '#a8bc3d',
    limeLight: '#d8e87a',
    // Semantic
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    purple: '#7c3aed',
    // Alpha variants (for overlays, backgrounds)
    navy05: 'rgba(55, 80, 147, 0.05)',
    navy08: 'rgba(55, 80, 147, 0.08)',
    navy10: 'rgba(55, 80, 147, 0.1)',
    navy15: 'rgba(55, 80, 147, 0.15)',
    navy20: 'rgba(55, 80, 147, 0.2)',
    navy40: 'rgba(55, 80, 147, 0.4)',
    lime03: 'rgba(201, 221, 81, 0.03)',
    lime08: 'rgba(201, 221, 81, 0.08)',
    lime15: 'rgba(201, 221, 81, 0.15)',
    lime18: 'rgba(201, 221, 81, 0.18)',
    lime20: 'rgba(201, 221, 81, 0.2)',
    lime45: 'rgba(201, 221, 81, 0.45)',
};
export const yoloRgb = {
    navy: '55 80 147',
    navyDark: '42 61 112',
    navyLight: '74 107 184',
    lime: '201 221 81',
    limeDark: '168 188 61',
    limeLight: '216 232 122',
};
// =============================================================================
// Semantic / Status Colors
// =============================================================================
export const semantic = {
    success: '#4caf50',
    successLight: '#e8f5e9',
    successStrong: '#1f9d55',
    successText: '#1B5E20',
    successBgDark: 'rgba(76, 175, 80, 0.18)',
    successBorderDark: 'rgba(76, 175, 80, 0.35)',
    successTextDark: '#95de64',
    warning: '#fbc02d',
    warningLight: '#fff8e1',
    warningLightAlt: '#fef3c7', // amber-100 - for playability/badges
    warningText: '#BF360C',
    warningBgDark: 'rgba(251, 192, 45, 0.18)',
    warningBorderDark: 'rgba(251, 192, 45, 0.35)',
    warningTextDark: '#ffd666',
    error: '#e53935',
    errorLight: '#ffebee',
    errorLightAlt: '#fee2e2', // red-100 - for playability/badges
    errorText: '#B71C1C',
    errorBgDark: 'rgba(229, 57, 53, 0.18)',
    errorBorderDark: 'rgba(229, 57, 53, 0.35)',
    errorTextDark: '#ff7875',
    danger: '#d9534f',
    dangerStrong: '#b91c1c',
    info: '#4397ae',
    infoLight: '#e8f4f8',
    infoText: '#0B6478',
    infoBgDark: 'rgba(67, 151, 174, 0.18)',
    infoBorderDark: 'rgba(67, 151, 174, 0.35)',
    infoTextDark: '#69c0ff',
    // UV severity (weather-specific)
    uvExtreme: '#7c3aed', // purple-600 - for extreme UV index
};
// =============================================================================
// Surface / Background Colors
// =============================================================================
export const surface = {
    background: '#f3f8f2',
    surface: '#ffffff',
    surfaceVariant: '#f5f5f5',
    onSurface: '#111111',
    onSurfaceVariant: '#666666',
};
// =============================================================================
// Neutral Colors
// =============================================================================
export const neutral = {
    white: '#ffffff',
    black: '#000000',
    shadow: '#000000',
    // Named neutrals (DigiWedge brand)
    blackOlive: '#3F4739',
    jet: '#30323D',
    charcoal: '#274156',
    mintCream: '#F3F8F2',
    midnightGreen: '#114B5F',
    eeZero: '#e0e0e0',
};
// =============================================================================
// Text Colors
// =============================================================================
export const text = {
    primary: '#111111',
    secondary: '#444444',
    muted: '#888888',
    placeholder: '#77939f',
};
// =============================================================================
// Border / Outline Colors
// =============================================================================
export const border = {
    outline: '#e0e0e0',
    border: '#eeeeee',
    divider: '#e3e3e3',
    light: '#eeeeee',
};
// =============================================================================
// Gray Scale
// =============================================================================
export const gray = {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
};
// =============================================================================
// Overlay Colors
// =============================================================================
export const overlay = {
    dark: 'rgba(0, 0, 0, 0.4)',
    light: 'rgba(234, 234, 234, 0.13)',
    modal: 'rgba(0, 0, 0, 0.5)',
    // Error state overlays
    errorBg: 'rgba(220, 38, 38, 0.1)', // inline error background
    // White overlays for banners
    whiteSoft: 'rgba(255, 255, 255, 0.8)', // text on dark backgrounds
    whiteSubtle: 'rgba(255, 255, 255, 0.9)', // slightly more visible
    whiteMuted: 'rgba(255, 255, 255, 0.2)', // very subtle white
};
export const shadows = {
    lightPrimary: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
    lightSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
    darkPrimary: '0 1px 2px 0 rgba(0, 0, 0, 0.28), 0 1px 6px -1px rgba(0, 0, 0, 0.24), 0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    darkSecondary: '0 8px 24px 0 rgba(0, 0, 0, 0.32), 0 6px 16px -4px rgba(0, 0, 0, 0.28), 0 12px 32px 8px rgba(0, 0, 0, 0.18)',
};
// =============================================================================
// Golf-Specific Semantic Colors
// =============================================================================
export const golf = {
    weather: {
        excellent: '#22c55e', // green-500 - Perfect conditions
        good: '#84cc16', // lime-500 - Good conditions
        fair: '#eab308', // yellow-500 - Playable
        poor: '#f97316', // orange-500 - Not recommended
        unplayable: '#ef4444', // red-500 - Course closed
    },
    rating: {
        active: '#facc15', // yellow-400 - Filled star
        inactive: '#d1d5db', // gray-300 - Empty star
    },
    handicap: {
        improving: '#22c55e', // green-500 - Trend down
        stable: '#6b7280', // gray-500 - No change
        declining: '#ef4444', // red-500 - Trend up
    },
    course: {
        available: '#22c55e', // green-500 - Slot available
        limited: '#f97316', // orange-500 - Few slots left
        soldOut: '#ef4444', // red-500 - No availability
    },
};
// =============================================================================
// RGB Values (for Tailwind alpha support)
// =============================================================================
/**
 * Convert hex to space-separated RGB values for Tailwind alpha utilities.
 * Example: '#6aae43' -> '106 174 67'
 */
export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result)
        return '0 0 0';
    return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`;
}
export const rgb = {
    primary: hexToRgb(brand.primary),
    primaryLight: hexToRgb(brand.primaryLight),
    secondary: hexToRgb(brand.secondary),
    success: hexToRgb(semantic.success),
    successLight: hexToRgb(semantic.successLight),
    warning: hexToRgb(semantic.warning),
    warningLight: hexToRgb(semantic.warningLight),
    error: hexToRgb(semantic.error),
    errorLight: hexToRgb(semantic.errorLight),
    info: hexToRgb(semantic.info),
    infoLight: hexToRgb(semantic.infoLight),
    blackOlive: hexToRgb(neutral.blackOlive),
    jet: hexToRgb(neutral.jet),
    charcoal: hexToRgb(neutral.charcoal),
    mintCream: hexToRgb(neutral.mintCream),
    midnightGreen: hexToRgb(neutral.midnightGreen),
    eeZero: hexToRgb(neutral.eeZero),
};
// =============================================================================
// Flat Color Export (for backward compatibility)
// =============================================================================
export const colors = {
    // Brand
    primary: brand.primary,
    primaryLight: brand.primaryLight,
    primaryHover: brand.primaryHover,
    primaryActive: brand.primaryActive,
    secondary: brand.secondary,
    onPrimary: brand.onPrimary,
    onSecondary: brand.onSecondary,
    primaryAlpha10: brandAlpha.primary10,
    primaryAlpha15: brandAlpha.primary15,
    // Semantic
    success: semantic.success,
    successLight: semantic.successLight,
    successStrong: semantic.successStrong,
    successBgDark: semantic.successBgDark,
    successBorderDark: semantic.successBorderDark,
    successTextDark: semantic.successTextDark,
    warning: semantic.warning,
    warningLight: semantic.warningLight,
    warningLightAlt: semantic.warningLightAlt,
    warningBgDark: semantic.warningBgDark,
    warningBorderDark: semantic.warningBorderDark,
    warningTextDark: semantic.warningTextDark,
    error: semantic.error,
    errorLight: semantic.errorLight,
    errorLightAlt: semantic.errorLightAlt,
    errorBgDark: semantic.errorBgDark,
    errorBorderDark: semantic.errorBorderDark,
    errorTextDark: semantic.errorTextDark,
    danger: semantic.danger,
    dangerStrong: semantic.dangerStrong,
    info: semantic.info,
    infoLight: semantic.infoLight,
    infoBgDark: semantic.infoBgDark,
    infoBorderDark: semantic.infoBorderDark,
    infoTextDark: semantic.infoTextDark,
    uvExtreme: semantic.uvExtreme,
    // Surface
    background: surface.background,
    surface: surface.surface,
    surfaceVariant: surface.surfaceVariant,
    onSurface: surface.onSurface,
    onSurfaceVariant: surface.onSurfaceVariant,
    // Neutral
    white: neutral.white,
    black: neutral.black,
    shadow: neutral.shadow,
    blackOlive: neutral.blackOlive,
    jet: neutral.jet,
    charcoal: neutral.charcoal,
    mintCream: neutral.mintCream,
    midnightGreen: neutral.midnightGreen,
    eeZero: neutral.eeZero,
    // Text
    textPrimary: text.primary,
    textSecondary: text.secondary,
    textMuted: text.muted,
    textPlaceholder: text.placeholder,
    // Border
    outline: border.outline,
    border: border.border,
    divider: border.divider,
    // Gray scale
    gray50: gray[50],
    gray100: gray[100],
    gray200: gray[200],
    gray300: gray[300],
    gray400: gray[400],
    gray500: gray[500],
    gray600: gray[600],
    gray700: gray[700],
    gray800: gray[800],
    gray900: gray[900],
    // Overlay
    overlay: overlay.dark,
    overlayLight: overlay.light,
    overlayModal: overlay.modal,
    overlayErrorBg: overlay.errorBg,
    overlayWhiteSoft: overlay.whiteSoft,
    overlayWhiteSubtle: overlay.whiteSubtle,
    overlayWhiteMuted: overlay.whiteMuted,
    shadowLightPrimary: shadows.lightPrimary,
    shadowLightSecondary: shadows.lightSecondary,
    shadowDarkPrimary: shadows.darkPrimary,
    shadowDarkSecondary: shadows.darkSecondary,
};
//# sourceMappingURL=colors.js.map